import { ApplicationConfig, LOCALE_ID, importProvidersFrom } from '@angular/core';
import { TitleStrategy } from '@angular/router';
import { BefretTitleStrategy } from './core/befret-title-strategy';
import { provideHttpClient, withInterceptorsFromDi, HttpClient } from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';

import { CoreModule } from './core/core.module';
import { AppRoutingModule } from './app-routing.module';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { environment } from './environment';

// Firebase
import { provideFirebaseApp, initializeApp } from '@angular/fire/app';
import { provideAuth, getAuth } from '@angular/fire/auth';
import { provideAnalytics, getAnalytics, ScreenTrackingService, UserTrackingService } from '@angular/fire/analytics';
import { provideFirestore, getFirestore } from '@angular/fire/firestore';
import { provideFunctions, getFunctions } from '@angular/fire/functions';
import { provideMessaging, getMessaging } from '@angular/fire/messaging';
import { provideStorage, getStorage } from '@angular/fire/storage';
import { provideCharts, withDefaultRegisterables } from 'ng2-charts';

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, '/assets/i18n/', '.json');
}

/**
 * 🚀 Configuration applicative racine (migration standalone, étape 8).
 * Remplace AppModule : bootstrapApplication(AppComponent, appConfig) dans main.ts.
 *
 * - `CoreModule` (importProvidersFrom) : singletons + enregistrement HTTP_INTERCEPTORS
 *   (cors + security), consommés via `provideHttpClient(withInterceptorsFromDi())`.
 * - `AppRoutingModule` (importProvidersFrom) : RouterModule.forRoot(routes, scroll config).
 *   L'ancien `RouterModule.forRoot([])` vide d'AppModule était vestigial → abandonné.
 * - `TranslateModule.forRoot` : loader HTTP i18n + defaultLanguage 'fr'.
 * - `NgbModule` : services ng-bootstrap (NgbModal, config…) disponibles app-wide.
 * - ⚠️ `provideFunctions` force `region = 'europe-west1'` — NE PAS retirer : sans ça,
 *   les callable visent us-central1 par défaut → toutes échouent (CORS). Vérifié par
 *   /pre-deploy-check et /cors-region-audit.
 * - Les MatXModule / ZXing / BaseChartDirective / CommonModule / Forms / SharedModule
 *   d'AppModule sont retirés : chaque composant standalone importe désormais ce qu'il
 *   utilise. `provideCharts` reste (enregistrement global ng2-charts).
 */
export const appConfig: ApplicationConfig = {
  providers: [
    importProvidersFrom(
      CoreModule,
      AppRoutingModule,
      NgbModule,
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient],
        },
        defaultLanguage: 'fr',
      }),
    ),
    provideAnimations(),
    provideHttpClient(withInterceptorsFromDi()),
    { provide: LOCALE_ID, useValue: 'fr' },
    // Titres d'onglet par route, suffixés par l'environnement (#600)
    { provide: TitleStrategy, useClass: BefretTitleStrategy },

    // Firebase
    provideFirebaseApp(() => initializeApp(environment.firebaseConfig)),
    provideAuth(() => getAuth()),
    provideAnalytics(() => getAnalytics()),
    ScreenTrackingService,
    UserTrackingService,
    provideFirestore(() => getFirestore()),
    provideFunctions(() => {
      const functions = getFunctions();
      // ⚠️ europe-west1 obligatoire (région de déploiement des Cloud Functions).
      // Sans ça → us-central1 par défaut → toutes les callable échouent (CORS).
      functions.region = 'europe-west1';
      return functions;
    }),
    provideMessaging(() => getMessaging()),
    provideStorage(() => getStorage()),
    provideCharts(withDefaultRegisterables()),
  ],
};
